import { MessageType, WebSocketMessage, WebSocketMessageHandler } from './WebSocketMessageHandler';

class SocketService {
  private static instance: SocketService;
  private socket: WebSocket | null = null;
  private messageHandler: WebSocketMessageHandler;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;

  private constructor() {
    this.messageHandler = WebSocketMessageHandler.getInstance();
  }

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public connect(url: string, token?: string | null): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already connected.');
      return;
    }

    const connectionUrl = token ? `${url}?token=${token}` : url;
    this.socket = new WebSocket(connectionUrl);

    this.socket.onopen = () => {
      console.log('Connected to WebSocket server');
      this.reconnectAttempts = 0;
    };

    this.socket.onclose = (event) => {
      console.log('Disconnected from WebSocket server:', event.code, event.reason);
      this.handleReconnect(url, token);
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (this.messageHandler.isValidMessage(data)) {
          this.messageHandler.handleMessage(data);
        } else {
          console.warn('Received invalid message format:', data);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    };
  }

  private handleReconnect(url: string, token?: string | null): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
      
      setTimeout(() => {
        this.connect(url, token);
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      console.error('Max reconnection attempts reached');
    }
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  public sendMessage(message: WebSocketMessage): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
    }
  }

  public addMessageHandler(type: MessageType, handler: (message: WebSocketMessage) => void): void {
    this.messageHandler.addMessageHandler(type, handler);
  }

  public removeMessageHandler(type: MessageType, handler: (message: WebSocketMessage) => void): void {
    this.messageHandler.removeMessageHandler(type, handler);
  }

  public isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  public getConnectionState(): string {
    if (!this.socket) return 'disconnected';
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING: return 'connecting';
      case WebSocket.OPEN: return 'connected';
      case WebSocket.CLOSING: return 'closing';
      case WebSocket.CLOSED: return 'closed';
      default: return 'unknown';
    }
  }

  // Convenience methods for common message types
  public joinQueue(): void {
    this.sendMessage(this.messageHandler.createMessage(MessageType.JOIN_QUEUE));
  }

  public leaveQueue(): void {
    this.sendMessage(this.messageHandler.createMessage(MessageType.LEAVE_QUEUE));
  }

  public playCard(gameId: string, cardId: string): void {
    this.sendMessage(this.messageHandler.createMessage(MessageType.PLAY_CARD, { gameId, cardId }));
  }

  public getGameState(gameId: string): void {
    this.sendMessage(this.messageHandler.createMessage(MessageType.GET_GAME_STATE, { gameId }));
  }

  public startCombat(attackerId: string, defenderId: string, gameId: string): void {
    this.sendMessage(this.messageHandler.createMessage(MessageType.START_COMBAT, { 
      attackerId, 
      defenderId, 
      gameId 
    }));
  }

  public revealTile(gameId: string, x: number, y: number): void {
    this.sendMessage(this.messageHandler.createMessage(MessageType.REVEAL_TILE, { gameId, x, y }));
  }
}

const socketService = SocketService.getInstance();
export default socketService;